import React, { useMemo } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { Theme } from '../types';
import { siteConfig } from '../config/site';
import { getNavLinks } from '../config/nav';
import SiteHeader from './SiteHeader';
import BrandLockup from './BrandLockup';

interface LayoutProps {
    theme: Theme;
    onToggleTheme: () => void;
}

const Layout: React.FC<LayoutProps> = ({ theme, onToggleTheme }) => {
    const navLinks = getNavLinks();
    const year = useMemo(() => new Date().getFullYear(), []);

    return (
        <div className="interior">
            {/* Halftone newsprint texture, shared with the front page */}
            <div className="fp-texture" aria-hidden="true" />

            {/* Skip to content (visible only on keyboard focus) */}
            <a
                href="#main-content"
                className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:bg-[var(--fp-magenta)] focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-[var(--fp-paper)]"
            >
                Skip to content
            </a>

            {/* Standing masthead — shared with the landing page */}
            <SiteHeader theme={theme} onToggleTheme={onToggleTheme} />

            {/* Main Content */}
            <main id="main-content" className="interior__main">
                <Outlet />
            </main>

            {/* Colophon */}
            <footer className="interior__foot" aria-label="Site footer">
                {/* Main columns */}
                <div className="interior__foot-main">
                    <div className="interior__foot-brand">
                        <BrandLockup size={40} wordSize={22} className="interior__foot-brand-name" />
                        <p className="interior__foot-brand-tagline">The picks publication for fans who actually watch the games.</p>
                    </div>

                    <div className="interior__foot-col">
                        <span className="interior__foot-col-head">Explore</span>
                        <nav aria-label="Footer navigation">
                            {navLinks.map(link => (
                                <Link key={link.path} to={link.path}>{link.label}</Link>
                            ))}
                        </nav>
                    </div>

                    <div className="interior__foot-col">
                        <span className="interior__foot-col-head">Legal</span>
                        <nav aria-label="Legal links">
                            <Link to="/privacy">Privacy Policy</Link>
                            <Link to="/terms">Terms of Use</Link>
                            <Link to="/accessibility">Accessibility</Link>
                            <Link to="/data-sources">Data & Sources</Link>
                            {siteConfig.contactEmail && (
                                <a href={`mailto:${siteConfig.contactEmail}`}>Contact</a>
                            )}
                        </nav>
                    </div>
                </div>

                {/* Copyright bar */}
                <div className="interior__foot-bar">
                    <span>© {year} <b>2 Girls 1 Parlay</b> · Entertainment, not advice · 21+</span>
                    <span>Gambling problem? <b>1-800-GAMBLER</b></span>
                </div>

                {/* Responsible gambling fine print */}
                <div className="interior__foot-legal">
                    <p>2 Girls 1 Parlay provides entertainment content only and does not facilitate, broker, or advise gambling. Content is intended for adults 21+ in jurisdictions where legal. If gambling is causing harm, call 1-800-GAMBLER · NY: 877-8-HOPENY / Text HOPENY · CT: gamblinghelp.org · MA: gamblinghelplinema.org.</p>
                </div>
            </footer>
        </div>
    );
};

export default Layout;
